import React, { useMemo } from 'react';
import '../contract-document.css';

/**
 * Render a contract template body with field values + signatures interpolated.
 * Props:
 *  - bodyHtml: string (template HTML with {{field_key}} and <div class="sig-slot" data-slot="signer|witness|ica_signer">)
 *  - fieldValues: { [key]: string }
 *  - signatures: { signer?: dataUrl, witness?: dataUrl, ica_signer?: dataUrl }
 *  - logoUrl: optional override for the header logo (latest from settings)
 *  - senderBlockHtml: optional HTML appended at the bottom (per-template footer)
 */
const ContractDocument = ({ bodyHtml, fieldValues = {}, signatures = {}, logoUrl, senderBlockHtml }) => {
  const rendered = useMemo(() => {
    let html = bodyHtml || '';

    // If a fresh logoUrl is supplied, strip any embedded .contract-header
    // (which carries a stale logo URL frozen at seed-time) and replace it.
    if (logoUrl) {
      html = html.replace(/<div\s+class="contract-header"[\s\S]*?<\/div>/i, '');
      html = `<div class="contract-header"><img class="contract-logo" src="${logoUrl}" alt="Institute of Culinary Arts" /></div>` + html;
    }

    // Replace text placeholders. Fields whose key ends in `_block` or `_html`
    // are treated as raw HTML (e.g. the auto-rendered Industry-letter students
    // table). All other fields are HTML-escaped to prevent injection.
    // For empty values: use a plain italic placeholder (NOT another .field-inline
    // span) — the template body already wraps {{key}} in a .field-inline, so a
    // second one would render as a nested double-bordered box overlapping the
    // surrounding text.
    html = html.replace(/\{\{(\w+)\}\}/g, (_match, key) => {
      const value = fieldValues[key];
      if (value && String(value).trim()) {
        if (/_(block|html)$/i.test(key)) {
          return String(value);
        }
        return escapeHtml(String(value));
      }
      return `<em class="field-placeholder">(${humanise(key)})</em>`;
    });
    // Inject signature images into sig-slots
    html = html.replace(
      /<div\s+class="sig-slot"\s+data-slot="(signer|witness|ica_signer)"[^>]*><\/div>/g,
      (_match, slot) => {
        const dataUrl = signatures[slot];
        if (dataUrl) {
          return `<div class="sig-slot"><img src="${dataUrl}" alt="${slot} signature" /></div>`;
        }
        return `<div class="sig-slot"></div>`;
      }
    );
    // Optional per-template footer (sender block) — appended after signatures
    if (senderBlockHtml && senderBlockHtml.trim()) {
      html += `<div class="contract-sender-block">${senderBlockHtml}</div>`;
    }

    return html;
  }, [bodyHtml, fieldValues, signatures, logoUrl, senderBlockHtml]);

  return (
    <div
      data-testid="contract-document"
      className="contract-document"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
};

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const humanise = (key) =>
  String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default ContractDocument;
